import { Button } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  EmptyIndicator,
  TomTable,
  tomTableColumnFilterPresets,
  useDefaultMRTOptions,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { AllocationRuleDto, YardBlockType } from '@yard/app/api'
import { useYardStores } from '@yard/AppProvider'
import {
  mapAllocationRuleDestinationToYardPositionDescriptor,
  mapAllocationRuleFacetsToString,
} from '@yard/components/AllocationRules/helpers/allocation-rule.mapper'
import { getSlotCountByDestination } from '@yard/utils/yard-block.util'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'

interface Props {
  allocationRules: AllocationRuleDto[]
  onCopy: (rule: AllocationRuleDto) => void
}

export const AllocationRulesTable = observer(({ allocationRules, onCopy }: Props) => {
  const { t } = useTranslate()
  const { yardBlockStore } = useYardStores()
  const defaultOptions = useDefaultMRTOptions<AllocationRuleDto>()

  const columns = useMemo<MRT_ColumnDef<AllocationRuleDto>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('name', 'Name'),
        ...tomTableColumnFilterPresets.searchMandatory(),
        size: 140,
      },
      {
        accessorKey: 'facets',
        header: t('fields', 'Fields'),
        accessorFn: row => mapAllocationRuleFacetsToString(row.facets, t, row.enforcementMode),
        ...tomTableColumnFilterPresets.searchOptional(),
        muiTableBodyCellProps: {
          sx: {
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          },
        },
      },
      {
        accessorKey: 'destinations',
        header: t('destinations', 'Destinations'),
        size: 140,
        accessorFn: row => {
          const destinations = row.destinations.map(dest => {
            const block = yardBlockStore.yardBlocks[dest.blockId]
            return mapAllocationRuleDestinationToYardPositionDescriptor(
              block,
              dest.minBay,
              dest.maxBay,
              dest.minRow,
              dest.maxRow,
            )
          })

          return destinations.join(', ')
        },
        ...tomTableColumnFilterPresets.searchOptional(),
        muiTableBodyCellProps: {
          sx: {
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          },
        },
      },
      {
        accessorKey: 'availableSlots',
        header: t('availableSlots', 'Available slots'),
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableSorting: false,
        size: 75,
        accessorFn: row => {
          let count = 0

          if (
            row.destinations.every(
              dest => yardBlockStore.yardBlocks[dest.blockId].type === YardBlockType.FlexibleBlock,
            )
          )
            return EmptyIndicator

          row.destinations.forEach(dest => {
            const block = yardBlockStore.yardBlocks[dest.blockId]
            if (block?.type === YardBlockType.FlexibleBlock) return

            count += getSlotCountByDestination(
              yardBlockStore.items,
              dest.blockId,
              dest.minBay,
              dest.maxBay,
              dest.minRow,
              dest.maxRow,
            )
          })
          return count
        },
      },
    ],
    [t, yardBlockStore],
  )

  const { savedPreferences, onPreferencesChange } =
    useTablePreferencesConfig('yard/allocation-rules')

  const table = useTomTable<AllocationRuleDto>(
    {
      columns,
      data: allocationRules,
      enableExpanding: false,
      layoutMode: 'grid',
      enableRowSelection: false,
      renderRowActions: ({ row }) => {
        return (
          <Button variant='text' onClick={() => onCopy(row.original)}>
            {t('copyToRules', 'Copy to rules')}
          </Button>
        )
      },
      displayColumnDefOptions: {
        ...defaultOptions.displayColumnDefOptions,
        'mrt-row-actions': {
          grow: true,
          header: '',
        },
      },
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 500px)',
          minHeight: 'calc(100vh - 500px)',
        },
      },
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return <TomTable table={table} />
})
